import { ResponsiveBar } from '@nivo/bar';

import theme from 'utils/theme';

import Pluralize from 'components/Pluralize';

const COLOR = 'Color';
const OPTIONAL = 'Optional';

const fontFamilyString = theme.fontFamily.sans.join(',');
const optional = obj => obj.data[obj.pid + OPTIONAL];

const colors = [
  '#f47560', // pink
  '#f1e15b', // yellow
  '#e8a838', // orange
  '#61cdbb', // teal
  '#97e3d5', // light teal
  '#e8c1a0', // cream
  '#f14c30', // darker red
  '#edd82c', // darker yellow
  '#d49019' // darker orange
];

const defs = [
  {
    id: 'dots',
    type: 'patternDots',
    background: 'inherit',
    color: '#38bcb2',
    size: 4,
    padding: 1,
    stagger: true
  },
  {
    id: 'lines',
    type: 'patternLines',
    background: 'inherit',
    color: '#eed312',
    rotation: -45,
    lineWidth: 6,
    spacing: 10
  }
];

const barTheme = {
  tooltip: {
    container: {
      backgroundColor: theme.colors.pink[300],
      borderRadius: theme.borderRadius['sm'],
      boxShadow: theme.boxShadow.lg,
      fontFamily: fontFamilyString
    }
  }
};

const Label = props => (
  <>
    <Pluralize singular="mo" count={props.value} tag={null} />
    {optional(props) ? '*' : ''}
  </>
);

const Tooltip = props => (
  <div className="p-2 text-gray-900 bg-pink-300 text-sm">
    Rotate at <span className="font-semibold">{props.id}</span> for{' '}
    <Pluralize singular="month" count={props.value} />
    <i>{optional(props) && ' (optional)'}</i>
  </div>
);

const BarGraph = ({ data }) => {
  let secondLabel, fourthLabel;

  const keys = data.reduce((a, { siteName }) => [...a, siteName], []);
  const siteData = data.reduce((acc, cur, idx) => {
    cur.rotationMonths.map((val, i) => {
      acc[i] = {
        ...acc[i],
        year: i + 1,
        // Creates base key required by component (e.g. "Stanford University")
        [cur.siteName]: Number(val),
        // Adds whether rotation optional (e.g. "Stanford UniversityOptional")
        [cur.siteName + OPTIONAL]: cur.requiredrotation !== 'Yes',
        // Custom color (e.g. "Stanford UniversityColor")
        [cur.siteName + COLOR]: colors[idx % colors.length]
      };
      if (idx === 1) secondLabel = cur.siteName;
      if (idx === 3) fourthLabel = cur.siteName;
    });
    return acc;
  }, []);

  return (
    <div
      id="responsive-bar"
      className="w-full h-full border border-pink-200 bg-pink-100 py-3"
    >
      <ResponsiveBar
        animate={false}
        axisBottom={{ tickSize: 0, format: value => `Year ${value}` }}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        colors={({ id, data }) => data[id + COLOR]}
        data={siteData}
        defs={defs}
        enableGridY={false}
        fill={[
          { match: { id: secondLabel }, id: 'lines' },
          { match: { id: fourthLabel }, id: 'dots' }
        ]}
        indexBy="year"
        padding={0.05}
        keys={keys}
        label={o => <Label {...o} />}
        labelSkipHeight={12}
        labelSkipWidth={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 3]] }}
        layout="vertical"
        margin={{ top: 0, right: 0, bottom: 20, left: 0 }}
        motionDamping={15}
        motionStiffness={90}
        theme={barTheme}
        tooltip={obj => <Tooltip {...obj} />}
      />
      {/**
       * Ensures the bar graph is fully responsive and shrinks as the page
       * width narrows. Also applies consistent styling to svg text elements.
       */}
      <style jsx global>{`
        #responsive-bar svg {
          max-width: 100%;
        }
        #responsive-bar text {
          font-family: ${fontFamilyString} !important;
          font-size: ${theme.fontSize.xs} !important;
        }
      `}</style>
    </div>
  );
};

export default BarGraph;
